import { IntegrationsConfigEvent } from '@baemingo/integrations-config';
import { SerializedState as SerializedIntegrationsConfigStorage } from '@baemingo/integrations-config/dist/types';
import { ShoppingCartEvent } from '@baemingo/shopping-cart-events';
import { uniqBy } from 'lodash';

import { GetCompanyLicenses_company_licenses } from 'API/Companies/__queries__';
import { GetMainAppData_user_permissions } from 'API/MainAppData/__queries__';
import { GetCompany_Company } from 'Components/App/__queries__';

import * as actions from './actions';
import { Language } from './Hooks/useI18n';

export function company(
  state = localStorage[`${process.env.REACT_APP_PROJECT_ID}-companyId`] || null,
  action: any,
): string | null {
  switch (action.type) {
    case actions.SET_COMPANY:
      localStorage.setItem(`${process.env.REACT_APP_PROJECT_ID}-companyId`, action.company);
      return action.company;
    default:
      return state;
  }
}

interface CompanyDataAction {
  type: typeof actions.SET_COMPANY_DATA;
  companyData: GetCompany_Company | null;
}

export function companyData(
  state: GetCompany_Company | null = null,
  action: CompanyDataAction,
): GetCompany_Company | null {
  switch (action.type) {
    case actions.SET_COMPANY_DATA:
      return action.companyData;
    default:
      return state;
  }
}

export function notice(
  state = { message: '' },
  action: any,
): { message: string; autoHide?: number } {
  switch (action.type) {
    case actions.SHOW_NOTICE:
      return {
        message: action.message,
        autoHide: action.autoHide,
      };
    default:
      return state;
  }
}

export function drawer(state = false, action: any) {
  switch (action.type) {
    case actions.SHOW_DRAWER:
      return true;
    case actions.HIDE_DRAWER:
      return false;
    default:
      return state;
  }
}

export function userRole(state = '', action: any): string {
  switch (action.type) {
    case actions.SET_USER_ROLE:
      return action.role;
    default:
      return state;
  }
}

export function userId(state = '', action: any): string {
  switch (action.type) {
    case actions.SET_USER_ID:
      return action.userId;
    default:
      return state;
  }
}

// BAEMINGO or BARCONTROL
export function superAdminUserType(state = '', action: any): string {
  switch (action.type) {
    case actions.SET_SUPER_ADMIN_USER_TYPE:
      return action.superAdminUserType;
    default:
      return state;
  }
}

export function authToken(
  state = (localStorage[`${process.env.REACT_APP_PROJECT_ID}-idToken`] as string | undefined) ||
    null,
  action: any,
): string | null {
  switch (action.type) {
    case actions.SET_TOKEN:
      return action.authToken;
    default:
      return state;
  }
}

export function subuserAuthToken(state = null, action: any): string | null {
  switch (action.type) {
    case actions.SET_SUBUSER_TOKEN:
      return action.authToken;
    default:
      return state;
  }
}

export function languages(state: string[] = [], action: any): string[] {
  switch (action.type) {
    case actions.SET_LANGUAGES:
      return action.languages;
    default:
      return state;
  }
}

export function BOLanguage(state: Language = 'en', action: any): Language {
  switch (action.type) {
    case actions.SET_BO_LANGUAGE:
      return action.BOLanguage;
    default:
      return state;
  }
}

export function currencies(state: string[] = [], action: any): string[] {
  switch (action.type) {
    case actions.SET_CURRENCIES:
      return action.currencies;
    default:
      return state;
  }
}

export function currency(state = '', action: any): string {
  switch (action.type) {
    case actions.SET_CURRENCY:
      return action.currency;
    default:
      return state;
  }
}

export function integrations(state: any | null = null, action: any) {
  switch (action.type) {
    case actions.SET_INTEGRATIONS:
      return action.integrations;
    default:
      return state;
  }
}

export function licenses(state: any | null = null, action: any) {
  switch (action.type) {
    case actions.SET_LICENSES:
      return action.licenses;
    default:
      return state;
  }
}

export function tempUser(state: any | null = null, action: any) {
  switch (action.type) {
    case actions.SET_TEMP_USER:
      return action.fields;
    default:
      return state;
  }
}

interface IntegrationField {
  id: string;
  model: string;
  type: string;
  formId: string;
  relatedId: string;
  venueId: string;
  provider: string;
  values: Array<{
    fieldId: string;
    value: string;
  }>;
}
export function integrationFields(state: IntegrationField[] = [], action: any) {
  switch (action.type) {
    case actions.SET_INTEGRATION_FIELDS:
      return action.fields;
    default:
      return state;
  }
}

interface AddIntegrationsConfigEventAction {
  type: 'ADD_CONFIG_EVENT';
  event: IntegrationsConfigEvent;
}

interface MarkIntegrationsConfigEventSentAction {
  type: 'MARK_EVENTS_SENT';
  ids: string[];
  sentAt: string;
}

export function integrationsConfigEvents(
  state: IntegrationsConfigEvent[] = [],
  action: any, // Can be of any type of event
): IntegrationsConfigEvent[] {
  switch (action.type) {
    case 'ADD_CONFIG_EVENT':
      return [...state, action.event];
    case actions.SET_COMPANY:
      return []; // Clear state on company switch
    case 'MARK_EVENTS_SENT':
      return state.map((event) => {
        if (action.ids.includes(event.eventId)) {
          return {
            ...event,
            sentAt: action.sentAt,
          };
        }
        return event;
      });
    default:
      return state;
  }
}

export interface IntegrationsConfigSnapshotState {
  snapshot: SerializedIntegrationsConfigStorage | null;
  loading: boolean;
  timestamp: string | null;
}

export function integrationsConfigSnapshot(
  state: IntegrationsConfigSnapshotState = {
    snapshot: null,
    loading: true,
    timestamp: null,
  },
  action: any,
) {
  switch (action.type) {
    case 'SET_CONFIG_SNAPSHOT':
      return {
        snapshot: action.snapshot,
        timestamp: action.timestamp,
        loading: false,
      };
    default:
      return state;
  }
}

interface AddShoppingCartEventAction {
  type: 'ADD_SHOPPING_CART_EVENT';
  event: ShoppingCartEvent;
}

interface AddShoppingCartEventsAction {
  type: 'ADD_SHOPPING_CART_EVENTS';
  events: ShoppingCartEvent[];
}

interface MarkShoppingCartEventSentAction {
  type: 'MARK_EVENTS_SENT';
  ids: string[];
  sentAt: string;
}

export function shoppingCartEvents(
  state: ShoppingCartEvent[] = [],
  action:
    | AddShoppingCartEventAction
    | AddShoppingCartEventsAction
    | MarkShoppingCartEventSentAction,
): ShoppingCartEvent[] {
  switch (action.type) {
    case 'ADD_SHOPPING_CART_EVENT':
      if (!action.event.cartId || !action.event.venueId) return state;
      return uniqBy([...state, action.event], 'eventId');
    case 'ADD_SHOPPING_CART_EVENTS':
      if (action.events.some((event) => !event.cartId || !event.venueId)) return state;
      return uniqBy([...state, ...action.events], 'eventId');
    case 'MARK_EVENTS_SENT':
      return state.map((event) => {
        if (action.ids.includes(event.eventId)) {
          return {
            ...event,
            sentAt: action.sentAt,
          };
        }

        return event;
      });
    default:
      return state;
  }
}

export function userPermissions(state: GetMainAppData_user_permissions | null = null, action: any) {
  switch (action.type) {
    case actions.SET_USER_PERMISSIONS:
      return action.permissions;
    default:
      return state;
  }
}

export interface State {
  company: string | null;
  companyData: GetCompany_Company | null;
  notice: {
    message: string;
    autoHide?: number;
  };
  drawer: boolean;
  userRole: string;
  authToken: string | null;
  subuserAuthToken: string | null;
  languages: string[];
  currencies: string[];
  currency: string;
  userId: string;
  superAdminUserType: 'BAEMINGO' | 'BARCONTROL' | 'SPAIN' | '';
  integrations: any | null;
  integrationFields: IntegrationField[];
  shoppingCartEvents: ShoppingCartEvent[];
  tempUser: any;
  BOLanguage: Language;
  integrationsConfigEvents: IntegrationsConfigEvent[];
  integrationsConfigSnapshot: IntegrationsConfigSnapshotState;
  licenses: GetCompanyLicenses_company_licenses[];
  userPermissions: GetMainAppData_user_permissions | null;
}
